// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

/**
 * Global configuration for the Stargate Dashboard frontend.
 * This module loads configuration from environment variables and provides it to the rest of the application.
 */

// Auto-detect protocol and set appropriate base URL
const getApiBaseUrl = () => {
  // If explicitly set via env var, use that
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // For both HTTP and HTTPS, use relative URL to go through proxy
  // This ensures the app works correctly behind the HTTPS proxy
  return '/api';
};

// Per-install host (WireGuard / host IP). Prefer the value injected at container start
// (window.RUNTIME_CONFIG.host, written by docker-entrypoint.sh from .env), then the
// build-time env (dev), then the hostname the browser used. Resolving this at RUNTIME
// is what lets ONE prebuilt image serve every install instead of building per host.
const getHost = () =>
  (typeof window !== 'undefined' && window.RUNTIME_CONFIG?.host) ||
  process.env.REACT_APP_HOST ||
  (typeof window !== 'undefined' && window.location?.hostname) ||
  'localhost';

const sanitizePortalUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw || /PLACEHOLDER_/i.test(raw)) return '';
  try {
    const u = new URL(raw);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return '';
    return raw.replace(/\/$/, '');
  } catch (_) {
    return '';
  }
};

// Default configuration values
const defaultConfig = {
  // API settings
  api: {
    baseUrl: getApiBaseUrl(),
    timeout: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10), // Increased to 30s to prevent timeouts when adding Stargates
  },

  // MQTT settings
  // All credentials come from environment variables or RUNTIME_CONFIG (set by docker-entrypoint.sh from .env)
  // NO hardcoded credentials - if not configured, MQTT will fail with clear error
  mqtt: {
    brokerHost:
      window.RUNTIME_CONFIG?.mqtt?.brokerHost ||
      process.env.REACT_APP_MQTT_BROKER_HOST ||
      getHost(),
    brokerPort: parseInt(process.env.REACT_APP_MQTT_BROKER_PORT || '9001', 10),
    clientId: `dashboard_frontend_${Math.random().toString(16).substr(2, 8)}`,
    // Credentials from RUNTIME_CONFIG (Docker) or process.env (build-time) - no hardcoded fallbacks
    username: window.RUNTIME_CONFIG?.mqtt?.username || process.env.REACT_APP_MQTT_USERNAME || null,
    password: window.RUNTIME_CONFIG?.mqtt?.password || process.env.REACT_APP_MQTT_PASSWORD || null,
    topicPrefix: process.env.REACT_APP_MQTT_TOPIC_PREFIX || 'starfleet',
    qos: parseInt(process.env.REACT_APP_MQTT_QOS || '1', 10),
    reconnectPeriod: parseInt(process.env.REACT_APP_MQTT_RECONNECT_PERIOD || '5000', 10),
    connectTimeout: parseInt(process.env.REACT_APP_MQTT_CONNECT_TIMEOUT || '60000', 10),
    maxReconnectAttempts: parseInt(process.env.REACT_APP_MQTT_MAX_RECONNECT_ATTEMPTS || '10', 10),
    keepalive: parseInt(process.env.REACT_APP_MQTT_KEEPALIVE || '60', 10),
    enableTestMessages: process.env.REACT_APP_MQTT_ENABLE_TEST_MESSAGES === 'true' || false,
  },

  // Authentication settings
  auth: {
    tokenKey: process.env.REACT_APP_AUTH_TOKEN_KEY || 'stargate_dashboard_token',
    refreshTokenKey:
      process.env.REACT_APP_AUTH_REFRESH_TOKEN_KEY || 'stargate_dashboard_refresh_token',
    expiryKey: process.env.REACT_APP_AUTH_EXPIRY_KEY || 'stargate_dashboard_token_expiry',
  },

  // UI settings
  ui: {
    theme: process.env.REACT_APP_UI_THEME || 'light',
    animationsEnabled: process.env.REACT_APP_UI_ANIMATIONS_ENABLED !== 'false',
    defaultPageSize: parseInt(process.env.REACT_APP_UI_DEFAULT_PAGE_SIZE || '10', 10),
    dateFormat: process.env.REACT_APP_UI_DATE_FORMAT || 'YYYY-MM-DD HH:mm:ss',
    refreshInterval: parseInt(process.env.REACT_APP_UI_REFRESH_INTERVAL || '30000', 10),
  },

  // Feature flags
  features: {
    debugEnabled: process.env.REACT_APP_FEATURE_DEBUG_ENABLED === 'true',
    mqttEnabled: process.env.REACT_APP_FEATURE_MQTT_ENABLED !== 'false',
    darkModeEnabled: process.env.REACT_APP_FEATURE_DARK_MODE_ENABLED !== 'false',
    aiEnabled:
      window.RUNTIME_CONFIG?.features?.aiEnabled ?? process.env.REACT_APP_AI_ENABLED === 'true',
  },

  portalUrl: sanitizePortalUrl(
    (typeof window !== 'undefined' && window.RUNTIME_CONFIG?.portalUrl) ||
      process.env.REACT_APP_PORTAL_URL ||
      process.env.PORTAL_URL ||
      ''
  ),

  sso: {
    enabled:
      typeof window !== 'undefined' && window.RUNTIME_CONFIG?.sso?.enabled != null
        ? !!window.RUNTIME_CONFIG.sso.enabled
        : process.env.REACT_APP_SSO_ENABLED !== 'false',
    breakGlassLogin:
      typeof window !== 'undefined' && window.RUNTIME_CONFIG?.sso?.breakGlassLogin != null
        ? !!window.RUNTIME_CONFIG.sso.breakGlassLogin
        : process.env.REACT_APP_BREAK_GLASS_LOGIN !== 'false',
  },
};

export const getPortalLoginUrl = () => {
  const base = getConfigValue('portalUrl') || '';
  if (!base) return '';
  return `${base}/login`;
};

export const getPortalLogoutUrl = () => {
  const base = getConfigValue('portalUrl') || '';
  if (!base) return '';
  return `${base}/auth/logout`;
};

export const getPortalSsoStartUrl = (redirectUrl) => {
  const base = getConfigValue('portalUrl') || '';
  if (!base || !redirectUrl) return '';
  return `${base}/login?next=${encodeURIComponent(redirectUrl)}`;
};

export const safePortalNextUrl = (next) => {
  const portalLogin = getPortalLoginUrl();
  const portalBase = getConfigValue('portalUrl') || '';
  const withLoggedOut = (href) => {
    try {
      const u = new URL(href, window.location.origin);
      u.searchParams.set('logged_out', '1');
      return u.href;
    } catch (_) {
      return href;
    }
  };
  if (!next) return withLoggedOut(portalLogin || '/login');
  try {
    const target = new URL(next, window.location.origin);
    if (portalBase) {
      const portalOrigin = new URL(portalBase).origin;
      if (target.origin === portalOrigin) {
        if (!target.pathname || target.pathname === '/') target.pathname = '/login';
        target.searchParams.set('logged_out', '1');
        return target.href;
      }
    }
  } catch (_) {}
  return withLoggedOut(portalLogin || '/login');
};
/**
 * Rust `env_bool("AI_ENABLED")` parity: only the boolean true or the string
 * "true" (any case) enables Fleet Chat. "1" / "yes" / "on" stay off so the
 * nav cannot appear while `/api/assistant/*` 404s.
 */
export function parseAiEnabledFlag(value) {
  if (value === true) return true;
  if (typeof value === 'string') return value.trim().toLowerCase() === 'true';
  return false;
}

/** True when this install chose Ollama / Fleet Chat at deploy time. */
export function isFleetChatEnabled() {
  if (typeof window !== 'undefined' && window.RUNTIME_CONFIG && 'features' in window.RUNTIME_CONFIG) {
    return parseAiEnabledFlag(window.RUNTIME_CONFIG.features?.aiEnabled);
  }
  return parseAiEnabledFlag(getConfigValue('features.aiEnabled', false));
}

/**
 * Get a configuration value by key.
 * @param {string} key - The configuration key in dot notation (e.g., 'mqtt.brokerHost').
 * @param {*} defaultValue - The default value to return if the key is not found.
 * @returns {*} The configuration value.
 */
export const getConfigValue = (key, defaultValue = null) => {
  const keys = key.split('.');
  let value = defaultConfig;

  try {
    for (const k of keys) {
      value = value[k];
    }
    return value === undefined ? defaultValue : value;
  } catch (error) {
    return defaultValue;
  }
};
